import React, { useEffect, useRef, useState } from 'react'
import { uploadImageToS3 } from '../utils/imageUpload'
import { Button } from './ui'

const ImageUpload = ({ onImageUrlChange }) => {
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [isCapturing, setIsCapturing] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const fileInputRef = useRef(null)
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const canvasRef = useRef(null)

  // Detect if user is on mobile device
  useEffect(() => {
    const checkMobile = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    }
    setIsMobile(checkMobile())
  }, [])

  const handleError = (error) => {
    console.error('Upload error:', error)
    if (error.message?.includes('credentials')) {
      setError('AWS configuration error. Please contact support.')
    } else if (error.message?.includes('NetworkError')) {
      setError('Network error. Please check your internet connection.')
    } else if (
      error.message?.includes('HEIC conversion failed') ||
      error.message?.includes('heic conversion failed')
    ) {
      setError(
        'Failed to convert HEIC image. Please try another image or format.'
      )
    } else {
      setError(error.message || 'Failed to upload image. Please try again.')
    }
  }

  const handleUploadSuccess = (imageUrl) => {
    // Clear any previous errors
    setError('')
    // Show success indicator briefly
    setUploadSuccess(true)
    setTimeout(() => setUploadSuccess(false), 3000)
    // Pass the image URL back to parent component
    onImageUrlChange(imageUrl)
  }

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validate file type - allow HEIC as well
    if (
      !file.type.startsWith('image/') &&
      !file.name.toLowerCase().endsWith('.heic') &&
      !file.name.toLowerCase().endsWith('.heif')
    ) {
      setError('Please select an image file.')
      return
    }

    // Validate file size (20MB limit)
    const MAX_SIZE = 20 * 1024 * 1024
    if (file.size > MAX_SIZE) {
      setError('Image size must be less than 20MB.')
      return
    }

    try {
      setError('')
      setUploading(true)

      const imageUrl = await uploadImageToS3(file)
      handleUploadSuccess(imageUrl)

      // Reset file input
      if (event.target) {
        event.target.value = ''
      }
    } catch (error) {
      handleError(error)
    } finally {
      setUploading(false)
    }
  }

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  // For mobiles that support capture attribute
  const takePictureWithNative = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.capture = 'environment'

    input.onchange = handleFileUpload
    input.click()
  }

  // Start camera for desktop/browsers that don't support capture
  const startCamera = async () => {
    try {
      setError('')
      setIsCapturing(true)

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(
          "Your browser doesn't support camera access. Try uploading a photo instead."
        )
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      })

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        streamRef.current = stream
      }
    } catch (error) {
      console.error('Camera error:', error)
      setError(
        error.message || 'Failed to access camera. Please check permissions.'
      )
      setIsCapturing(false)
    }
  }

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    setIsCapturing(false)
  }

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return

    try {
      setUploading(true)

      const video = videoRef.current
      const canvas = canvasRef.current

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight

      // Draw video frame to canvas
      const ctx = canvas.getContext('2d')
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

      // Convert to file
      canvas.toBlob(
        async (blob) => {
          if (!blob) {
            setError('Failed to capture image')
            setUploading(false)
            return
          }

          const file = new File([blob], 'camera-capture.jpg', {
            type: 'image/jpeg',
          })

          try {
            const imageUrl = await uploadImageToS3(file)
            handleUploadSuccess(imageUrl)
            stopCamera()
          } catch (error) {
            handleError(error)
          } finally {
            setUploading(false)
          }
        },
        'image/jpeg',
        0.9
      )
    } catch (error) {
      handleError(error)
      setUploading(false)
    }
  }

  const handleCameraClick = () => {
    if (isCapturing) {
      stopCamera()
      return
    }

    // On mobile, try to use native camera first
    if (isMobile) {
      takePictureWithNative()
    } else {
      startCamera()
    }
  }

  return (
    <div className="space-y-2">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      {uploadSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded text-sm">
          Image added successfully!
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <Button
          type="button"
          variant="tertiary"
          onClick={triggerFileInput}
          disabled={uploading || isCapturing}
          className="flex items-center gap-1"
        >
          {uploading ? (
            <span>Uploading...</span>
          ) : (
            <>
              <span className="text-md sm:text-lg">📁</span>
              <span>Choose Photo</span>
            </>
          )}
        </Button>

        <Button
          type="button"
          variant="tertiary"
          onClick={handleCameraClick}
          disabled={uploading}
          className="flex items-center gap-1"
        >
          <span className="text-md sm:text-lg">
            {isCapturing ? '✖️' : '📷'}
          </span>
          <span>{isCapturing ? 'Cancel' : 'Take Photo'}</span>
        </Button>

        {/* Hidden file input for selecting photos from gallery */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,image/heic,.heic,.heif"
          className="hidden"
          onChange={handleFileUpload}
          disabled={uploading || isCapturing}
        />
      </div>

      {/* Camera UI for desktop browsers */}
      {isCapturing && (
        <div className="mt-2 bg-gray-100 border rounded-lg p-3">
          <div className="relative">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full rounded-lg"
            />
            <div className="mt-2 flex justify-center">
              <Button
                type="button"
                variant="primary"
                onClick={capturePhoto}
                disabled={uploading}
              >
                {uploading ? 'Processing...' : 'Take Picture'}
              </Button>
            </div>
          </div>
          <canvas ref={canvasRef} className="hidden" />
        </div>
      )}
    </div>
  )
}

export default ImageUpload
